import React from 'react';
import Icon from '../../../components/AppIcon';

const LoadingState = () => {
  // Skeleton card component
  const SkeletonCard = () => (
    <div className="bg-white rounded-2xl shadow-elevation-2 border border-border overflow-hidden animate-pulse">
      <div className="flex flex-col md:flex-row">
        {/* Image Skeleton */}
        <div className="w-full md:w-80 h-64 md:h-72 bg-gradient-to-br from-background-light to-background-medium"> {/* Updated from blue-100 to teal-100 */}
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Image" size={48} className="text-text-secondary" /> {/* Updated from blue-200 */}
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="flex-1 p-6">
          <div className="h-full flex flex-col space-y-4">
            {/* Header Skeleton */}
            <div className="space-y-2">
              <div className="h-6 bg-background-medium rounded-lg w-3/4"></div> {/* Updated from blue-100 */}
              <div className="h-4 bg-background-light rounded-lg w-1/2"></div> {/* Updated from gray-100 */}
              <div className="h-4 bg-background-light rounded-lg w-2/3"></div> {/* Updated from gray-100 */}
            </div>

            {/* Description Skeleton */}
            <div className="space-y-2">
              <div className="h-3 bg-background-light rounded w-full"></div> {/* Updated from gray-100 */}
              <div className="h-3 bg-background-light rounded w-4/5"></div> {/* Updated from gray-100 */}
              <div className="h-3 bg-background-light rounded w-3/5"></div> {/* Updated from gray-100 */}
            </div>

            {/* Practitioner Skeleton */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-border rounded-full"></div> {/* Updated from gray-200 */}
              <div className="h-4 bg-background-light rounded w-1/3"></div> {/* Updated from gray-100 */}
            </div>

            {/* Tags Skeleton */}
            <div className="flex space-x-2">
              <div className="h-6 bg-background-medium rounded-full w-16"></div> {/* Updated from blue-100 */}
              <div className="h-6 bg-background-medium rounded-full w-20"></div> {/* Updated from blue-100 */}
              <div className="h-6 bg-background-medium rounded-full w-14"></div> {/* Updated from blue-100 */}
            </div>

            {/* Amenities Skeleton */}
            <div className="space-y-2">
              <div className="flex space-x-2">
                <div className="h-3 bg-background-light rounded w-16"></div> {/* Updated from gray-100 */}
                <div className="h-3 bg-background-light rounded w-20"></div> {/* Updated from gray-100 */}
              </div>
              <div className="flex space-x-2">
                <div className="h-3 bg-background-light rounded w-14"></div> {/* Updated from gray-100 */}
                <div className="h-3 bg-background-light rounded w-18"></div> {/* Updated from gray-100 */}
              </div>
            </div>

            {/* Buttons Skeleton */}
            <div className="flex space-x-3 mt-auto">
              <div className="flex-1 h-12 bg-background-medium rounded-lg"></div> {/* Updated from blue-100 */}
              <div className="w-12 h-12 bg-background-light rounded-lg"></div> {/* Updated from gray-100 */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Skeleton */}
        <div className="mb-6">
          <div className="h-8 bg-border rounded-lg w-1/2 mb-2 animate-pulse"></div> {/* Updated from gray-200 */}
          <div className="h-4 bg-background-light rounded-lg w-1/4 animate-pulse"></div> {/* Updated from gray-100 */}
        </div>

        {/* Controls Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center space-x-4">
            <div className="h-10 bg-border rounded-lg w-24 animate-pulse"></div> {/* Updated from gray-200 */}
            <div className="hidden md:flex h-10 bg-border rounded-lg w-32 animate-pulse"></div> {/* Updated from gray-200 */}
          </div>
          <div className="h-10 bg-border rounded-lg w-40 animate-pulse"></div> {/* Updated from gray-200 */}
        </div>

        {/* Loading Message */}
        <div className="text-center py-8 mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r
                          from-background-light to-background-medium rounded-full mb-4"> {/* Updated from blue-100 to teal-100 */}
            <Icon name="Loader2" size={24} className="text-primary animate-spin" /> {/* Updated from blue-500 */}
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Discovering Wellness Centers
          </h3>
          <p className="text-text-secondary">
            Finding the perfect healing experiences for your journey...
          </p>
        </div>

        {/* Skeleton Cards */}
        <div className="space-y-6">
          {[...Array(3)].map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>

        {/* Loading Progress */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 text-sm text-text-secondary">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div> {/* Updated from blue-400 */}
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" 
                   style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" 
                   style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>Loading wellness centers</span>
          </div>
        </div>

        {/* Motivational Quote */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-background-light to-background-medium rounded-2xl p-8 border border-border"> {/* Updated from blue-50 to teal-50 and blue-100 */}
            <div className="max-w-2xl mx-auto">
              <Icon name="Quote" size={32} className="text-primary mx-auto mb-4" /> {/* Updated from blue-400 */}
              <blockquote className="text-lg text-text-primary font-medium italic mb-4">
                "The groundwork for all happiness is good health, and the foundation of
                good health is wellness of mind, body, and spirit."
              </blockquote>
              <cite className="text-sm text-text-secondary">- Ancient Wellness Wisdom</cite>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingState;
